<template>
  <div class="robofinder-app">
    <div class="loading" v-if="loading">
      <img src='/images/loading.gif'>
    </div>

    <div v-if="!loading && categoriesLoaded" class="robofinder-content">
      <robofinder-questionnaire
        :categories="categories"
        :initial-params="initialParams"
        :current-step="currentStep"
        :result-count="resultCount"
        :translations="translationsData"
        :is-mobile="isMobile === 'true'"
        :is-signed-in="isSignedIn === 'true'"
        :locale="locale"
        @step-changed="handleStepChange"
        @selection-changed="handleSelectionChange"
        @save-result="handleSaveResult"
        @email-results="handleEmailResults"
        @close-request="handleExitRequest"
      />

      <robofinder-results
        v-if="shouldShowResults"
        :selections="selections"
        :api-results-url="apiResultsUrl"
        :is-signed-in="isSignedIn"
        :is-mobile="isMobile"
        :translations="translationsData"
        @result-count-changed="handleResultCountChange"
      />
    </div>

    <!--
      途中離脱確認モーダル
      - Step 1以降で表示（Step 0では表示しない）
      - トリガー: ロゴタップ、バツ印タップ、ブラウザバック
      - 「続ける」: モーダルを閉じて現在ステップを維持
      - 「終了する」: referrerUrl（robofinder開始前のページ）に遷移
      - 関連: handleExitRequest, confirmExit, cancelExit, navigateToReferrer
    -->
    <div class="exit-modal-overlay" v-if="showExitModal" @click="cancelExit">
      <div class="exit-modal" @click.stop>
        <h3>{{ translationsData.questionnaire.exit_modal_title }}</h3>
        <p>{{ translationsData.questionnaire.exit_modal_description }}</p>
        <div class="exit-modal-buttons">
          <button class="btn-stay" @click="cancelExit">
            {{ translationsData.questionnaire.exit_modal_stay }}
          </button>
          <button class="btn-leave" @click="confirmExit">
            {{ translationsData.questionnaire.exit_modal_leave }}
          </button>
        </div>
      </div>
    </div>

    <!--
      メール送信完了モーダル
      - Email resultsクリック後、送信成功時に表示
    -->
    <div class="email-sent-modal-overlay" v-if="showEmailSentModal" @click="closeEmailSentModal">
      <div class="email-sent-modal" @click.stop>
        <button class="close-button" @click="closeEmailSentModal">×</button>
        <h3>{{ translationsData.questionnaire.email_sent_title }}</h3>
        <p>{{ translationsData.questionnaire.email_sent_description }}</p>
      </div>
    </div>

    <!--
      コレクション保存完了モーダル
      - Save Resultクリック後、保存成功時に表示
    -->
    <div class="save-result-modal-overlay" v-if="showSaveResultModal" @click="closeSaveResultModal">
      <div class="save-result-modal" @click.stop>
        <img src="/images/MyCollection.gif" alt="" class="save-result-gif" />
        <h3>{{ translationsData.questionnaire.save_result_title }}</h3>
        <p v-html="translationsData.questionnaire.save_result_description"></p>
        <button class="go-to-profile-button" @click="goToProfile">
          {{ translationsData.questionnaire.go_to_profile }}
        </button>
      </div>
    </div>

    <!--
      未ログインユーザー向けSave Resultモーダル
      - 未ログイン時にSave Resultクリックで表示
    -->
    <div class="save-result-login-modal-overlay" v-if="showSaveResultLoginModal" @click="closeSaveResultLoginModal">
      <div class="save-result-login-modal" @click.stop>
        <button class="close-button" @click="closeSaveResultLoginModal">×</button>
        <h3>{{ translationsData.questionnaire.save_result_login_title }}</h3>
        <p v-html="translationsData.questionnaire.save_result_login_description"></p>
        <button class="login-button" @click="goToLogin">
          {{ translationsData.questionnaire.save_result_login_button }}
        </button>
        <div class="modal-gif" v-if="isMobile !== 'true'">
          <img src="/images/SaveYourResults.gif" alt="Save Your Results">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * RoboFinder メインアプリケーションコンポーネント
 *
 * RoboFinderはロボット検索機能で、ユーザーが質問に答えていくことで
 * 条件に合ったProduct（ロボットプロファイル）とPostを検索できる。
 *
 * 主な機能:
 * 1. カテゴリ選択による絞り込み検索（location, task, purpose の各L1/L2）
 * 2. Save Result: 検索結果を一括でコレクションに追加
 * 3. Email Results: 検索結果をメールで送信（1-20件の場合のみ表示）
 *
 * ログインフロー:
 * - 未ログイン状態でSave Result/Email Resultsをクリックすると、
 *   現在のURLをreturn_toパラメータに設定してログインページへリダイレクト
 * - ログイン後、ApplicationController#after_sign_in_path_forがセッションから
 *   return_toを読み取り、元のrobofinderページに戻る
 *
 * 関連ファイル:
 * - questionnaire.vue: 質問UI、save-result/email-resultsイベントを発火
 * - results.vue: 検索結果表示
 * - CollectionsController#bulk_add: Save Result API
 * - CollectionsController#email_results: Email Results API
 * - RobofinderMailer: メール送信
 * - SessionsController#store_return_to: ログイン時のreturn_to保存
 * - ApplicationController#after_sign_in_path_for: ログイン後リダイレクト
 */
const axios = require('axios');
import Vue from 'vue/dist/vue.esm'
import Buefy from 'buefy'
import 'buefy/dist/buefy.css'
import RobofinderQuestionnaire from './robofinder/questionnaire.vue'
import RobofinderResults from './robofinder/results.vue'

Vue.use(Buefy)

export default {
  components: {
    RobofinderQuestionnaire,
    RobofinderResults,
  },

  props: {
    initialParamsJson: String,
    apiMetaUrl: String,
    apiResultsUrl: String,
    isSignedIn: String,
    isMobile: String,
    translations: String,
    referrerUrl: String,
    locale: String,
  },

  data() {
    return {
      loading: true,
      categoriesLoaded: false,
      categories: {
        location: { layer1: [], layer2: [] },
        task: { layer1: [], layer2: [] },
        purpose: { layer1: [], layer2: [] }
      },
      initialParams: {},
      currentStep: 0,
      selections: {
        location_category: null,
        location_subcategory: null,
        task_category: null,
        task_subcategory: null,
        purpose_category: null,
        purpose_subcategory: null
      },
      resultCount: null,
      translationsData: {},
      showExitModal: false,
      showEmailSentModal: false,
      showSaveResultModal: false,
      showSaveResultLoginModal: false,
      savedUserId: null
    }
  },

  computed: {
    shouldShowResults() {
      // Always show results (including all results on cover page)
      return true;
    }
  },

  methods: {
    async fetchCategories() {
      try {
        const response = await axios.get(this.apiMetaUrl, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        this.categories = response.data.categories;
        this.categoriesLoaded = true;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching categories:', error);
        this.loading = false;
      }
    },

    handleStepChange(newStep) {
      const wasOnCover = this.currentStep === 0;
      this.currentStep = newStep;
      this.updateUrl();

      // Step 0→1移行時にダミー履歴追加（ブラウザバック検知用）
      if (wasOnCover && newStep >= 1) {
        history.pushState({ robofinder: true }, '', location.href);
      }
    },

    handleSelectionChange(selections) {
      this.selections = { ...selections };
      this.updateUrl();
    },

    handleResultCountChange(count) {
      this.resultCount = count;
    },

    updateUrl() {
      const url = new URL(window.location.href);
      const params = new URLSearchParams();

      // Add step parameter (only if not on cover page)
      if (this.currentStep > 0) {
        params.set('step', this.currentStep);
      }

      // Add selection parameters
      Object.keys(this.selections).forEach(key => {
        if (this.selections[key]) {
          params.set(key, this.selections[key]);
        }
      });

      const newUrl = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    },

    /**
     * Save Resultボタンがクリックされた時の処理
     * - ログイン済み: 検索結果をコレクションに一括追加
     * - 未ログイン: ログイン促進モーダルを表示
     * @param {Object} selections - 現在の選択状態
     */
    async handleSaveResult(selections) {
      // ログイン状態を確認
      if (this.isSignedIn !== 'true') {
        // 未ログインの場合: ログイン促進モーダルを表示
        this.showSaveResultLoginModal = true;
        return;
      }

      // ログイン済みの場合: APIを呼び出してコレクションに一括追加
      try {
        // 選択されたパラメータを構築
        const params = {};
        if (selections.location_category) params.location_category = selections.location_category;
        if (selections.location_subcategory) params.location_subcategory = selections.location_subcategory;
        if (selections.task_category) params.task_category = selections.task_category;
        if (selections.task_subcategory) params.task_subcategory = selections.task_subcategory;
        if (selections.purpose_category) params.purpose_category = selections.purpose_category;
        if (selections.purpose_subcategory) params.purpose_subcategory = selections.purpose_subcategory;

        // CSRFトークンを取得
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

        // ロケールを取得してAPIエンドポイントを構築
        const locale = this.locale || 'en';

        // 一括追加APIを呼び出し
        const response = await axios.post(`/${locale}/collections/bulk_add`, params, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });

        // 保存成功後、モーダルを表示
        this.savedUserId = response.data.user_id;
        this.showSaveResultModal = true;

      } catch (error) {
        console.error('Error saving results:', error);

        // 401エラー（セッション切れ）の場合はログインページへリダイレクト
        if (error.response && error.response.status === 401) {
          const currentUrl = window.location.pathname + window.location.search;
          const locale = this.locale || 'en';
          const loginUrl = `/${locale}/user/login?return_to=${encodeURIComponent(currentUrl)}`;
          window.location.href = loginUrl;
          return;
        }

        // TODO: エラー時のUI表示を実装する
        console.error('Failed to save results');
      }
    },

    /**
     * Email Resultsリンクがクリックされた時の処理
     * - ログイン済み: 検索結果をメールで送信
     * - 未ログイン: ログイン画面にリダイレクト（ログイン後に戻ってくる）
     * @param {Object} selections - 現在の選択状態
     */
    async handleEmailResults(selections) {
      // ログイン状態を確認
      if (this.isSignedIn !== 'true') {
        // 未ログインの場合: 現在のURLをreturn_toに設定してログインページへリダイレクト
        // ログイン後にこのページに戻ってくる（sessions_controller#store_return_toで処理）
        const currentUrl = window.location.pathname + window.location.search;
        const locale = this.locale || 'en';
        const loginUrl = `/${locale}/user/login?return_to=${encodeURIComponent(currentUrl)}`;
        window.location.href = loginUrl;
        return;
      }

      // ログイン済みの場合: APIを呼び出してメール送信
      try {
        // 選択されたパラメータを構築
        const params = {};
        if (selections.location_category) params.location_category = selections.location_category;
        if (selections.location_subcategory) params.location_subcategory = selections.location_subcategory;
        if (selections.task_category) params.task_category = selections.task_category;
        if (selections.task_subcategory) params.task_subcategory = selections.task_subcategory;
        if (selections.purpose_category) params.purpose_category = selections.purpose_category;
        if (selections.purpose_subcategory) params.purpose_subcategory = selections.purpose_subcategory;

        // CSRFトークンを取得
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

        // ロケールを取得してAPIエンドポイントを構築
        const locale = this.locale || 'en';

        // メール送信APIを呼び出し
        const response = await axios.post(`/${locale}/collections/email_results`, params, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });

        // 成功時: メール送信完了モーダルを表示
        this.showEmailSentModal = true;

      } catch (error) {
        console.error('Error sending email results:', error);

        // 401エラー（セッション切れ）の場合はログインページへリダイレクト
        if (error.response && error.response.status === 401) {
          const currentUrl = window.location.pathname + window.location.search;
          const locale = this.locale || 'en';
          const loginUrl = `/${locale}/user/login?return_to=${encodeURIComponent(currentUrl)}`;
          window.location.href = loginUrl;
          return;
        }

        // TODO: エラー時のUI表示を実装する
        console.error('Failed to send email results');
      }
    },

    // ========================================
    // 途中離脱確認モーダル関連メソッド
    // ========================================
    // トリガー:
    //   1. ロゴタップ (handleLogoClick)
    //   2. バツ印タップ (questionnaire.vue の handleClose → close-request イベント)
    //   3. ブラウザバック (handlePopState)
    //
    // フロー:
    //   handleExitRequest → showExitModal = true → モーダル表示
    //   ├─ 「続ける」→ cancelExit → showExitModal = false
    //   └─ 「終了する」→ confirmExit → navigateToReferrer → リファラーに遷移
    //
    // 関連ファイル:
    //   - robofinder_controller.rb: @referrer_url の設定
    //   - questionnaire.vue: handleClose で close-request イベント発火
    //   - view_en.yml / view_ja.yml: モーダルテキスト
    // ========================================

    /**
     * 離脱リクエスト処理
     * Step 1以降ではモーダルを表示、Step 0では直接遷移
     */
    handleExitRequest() {
      if (this.currentStep >= 1) {
        this.showExitModal = true;
      } else {
        this.navigateToReferrer();
      }
    },

    /**
     * 離脱確定（モーダルの「終了する」ボタン）
     */
    confirmExit() {
      this.navigateToReferrer();
    },

    /**
     * 離脱キャンセル（モーダルの「続ける」ボタン）
     */
    cancelExit() {
      this.showExitModal = false;
    },

    /**
     * メール送信完了モーダルを閉じる
     */
    closeEmailSentModal() {
      this.showEmailSentModal = false;
    },

    /**
     * コレクション保存完了モーダルを閉じる
     */
    closeSaveResultModal() {
      this.showSaveResultModal = false;
    },

    /**
     * 未ログインユーザー向けSave Resultモーダルを閉じる
     */
    closeSaveResultLoginModal() {
      this.showSaveResultLoginModal = false;
    },

    /**
     * ログインページへ遷移（未ログインユーザー向けモーダルのボタン）
     * ログイン後にこのページに戻ってくる
     */
    goToLogin() {
      const currentUrl = window.location.pathname + window.location.search;
      const locale = this.locale || 'en';
      const loginUrl = `/${locale}/user/login?return_to=${encodeURIComponent(currentUrl)}`;
      window.location.href = loginUrl;
    },

    /**
     * プロフィールページへ遷移（Save Result完了モーダルのボタン）
     * コレクションタブを初期選択状態にする
     */
    goToProfile() {
      const locale = this.locale || 'en';
      window.location.href = `/${locale}/users/${this.savedUserId}?tab=collections`;
    },

    /**
     * リファラーへ遷移
     * referrerUrl は robofinder_controller.rb で設定
     */
    navigateToReferrer() {
      window.location.href = this.referrerUrl || '/';
    },

    /**
     * ブラウザバック検知ハンドラ
     * pushStateでダミー履歴を追加し、popstateイベントで検知
     */
    handlePopState(event) {
      if (this.currentStep >= 1) {
        // 履歴を戻す（ブラウザバックをキャンセル）
        history.pushState({ robofinder: true }, '', location.href);
        this.handleExitRequest();
      }
    },

    /**
     * ロゴクリックハンドラ
     * グローバルヘッダーのロゴ（.header .logo a）にイベントリスナーを設定
     */
    handleLogoClick(event) {
      if (this.currentStep >= 1) {
        event.preventDefault();
        this.handleExitRequest();
      }
    }
  },

  mounted() {
    // Parse initial params from data attribute
    this.initialParams = JSON.parse(this.initialParamsJson || '{}');

    // Parse translations from data attribute
    this.translationsData = JSON.parse(this.translations || '{}');

    // Initialize selections from URL params
    this.selections = {
      location_category: this.initialParams.location_category || null,
      location_subcategory: this.initialParams.location_subcategory || null,
      task_category: this.initialParams.task_category || null,
      task_subcategory: this.initialParams.task_subcategory || null,
      purpose_category: this.initialParams.purpose_category || null,
      purpose_subcategory: this.initialParams.purpose_subcategory || null
    };

    // Initialize step from URL params (default to 0 for cover page)
    this.currentStep = parseInt(this.initialParams.step) || 0;

    // Fetch categories from API
    this.fetchCategories();

    // 途中離脱確認モーダル: ブラウザバック検知用イベントリスナー
    window.addEventListener('popstate', this.handlePopState);

    // 途中離脱確認モーダル: ロゴクリックインターセプト
    // グローバルヘッダーのロゴにイベントリスナーを設定
    const logoLink = document.querySelector('.header .logo a');
    if (logoLink) {
      logoLink.addEventListener('click', this.handleLogoClick);
    }

    // 途中離脱確認モーダル: Step 1以上で初期化された場合、ダミー履歴を追加
    // これによりブラウザバック時にpopstateイベントが発火する
    if (this.currentStep >= 1) {
      history.pushState({ robofinder: true }, '', location.href);
    }
  },

  beforeDestroy() {
    // 途中離脱確認モーダル: イベントリスナーのクリーンアップ
    window.removeEventListener('popstate', this.handlePopState);
    const logoLink = document.querySelector('.header .logo a');
    if (logoLink) {
      logoLink.removeEventListener('click', this.handleLogoClick);
    }
  }
}
</script>

<style lang="scss" scoped>
.robofinder-app {
  min-height: 400px;
  margin-top: 32px;

  .loading {
    text-align: center;
    padding: 50px;
  }

  .robofinder-content {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
    }
  }
}

.exit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.exit-modal {
  background-color: #fff;
  padding: 32px;
  border-radius: 0;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  h3 {
    font-size: 20px;
    margin: 0 0 16px 0;
    color: #333;
  }

  p {
    font-size: 16px;
    color: #666;
    margin: 0 0 24px 0;
  }

  .exit-modal-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;

    button {
      padding: 12px 24px;
      border-radius: 0;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-stay {
      background-color: #ff9933;
      color: #000;
      border: none;

      &:hover {
        background-color: #ff8800;
      }
    }

    .btn-leave {
      background-color: #fff;
      color: #333;
      border: 1px solid #ccc;

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }
}

.email-sent-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.email-sent-modal {
  background-color: #fff;
  padding: 32px;
  border-radius: 0;
  max-width: 400px;
  width: 90%;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  .close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    line-height: 1;
    padding: 0;

    &:hover {
      color: #333;
    }
  }

  h3 {
    font-size: 18px;
    font-weight: bold;
    margin: 0 0 16px 0;
    color: #333;
    padding-right: 24px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.6;
  }
}

.save-result-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.save-result-modal {
  background-color: #fff;
  padding: 32px;
  border-radius: 0;
  max-width: 400px;
  width: 90%;
  text-align: left;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  .save-result-gif {
    display: block;
    width: 300px;
    height: auto;
    margin: 0 auto 16px auto;
  }

  h3 {
    font-size: 20px;
    font-weight: bold;
    margin: 0 0 16px 0;
    color: #333;
  }

  p {
    font-size: 15px;
    color: #666;
    margin: 0 0 24px 0;
    line-height: 1.6;

    strong {
      color: #333;
      font-weight: bold;
    }
  }

  .go-to-profile-button {
    display: block;
    width: 100%;
    padding: 16px;
    border: none;
    border-radius: 0;
    background-color: #2E33CA;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s ease;

    &:hover {
      background-color: #2528a8;
    }
  }
}

.save-result-login-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.save-result-login-modal {
  background-color: #fff;
  padding: 32px;
  border-radius: 0;
  max-width: 400px;
  width: 90%;
  text-align: left;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;

  .close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
      color: #333;
    }
  }

  h3 {
    font-size: 20px;
    font-weight: bold;
    margin: 0 0 16px 0;
    color: #333;
    padding-right: 24px;
  }

  p {
    font-size: 15px;
    color: #666;
    margin: 0 0 24px 0;
    line-height: 1.6;
  }

  .login-button {
    display: block;
    width: 100%;
    padding: 16px;
    border: none;
    border-radius: 0;
    background-color: #2E33CA;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s ease;

    &:hover {
      background-color: #2528a8;
    }
  }

  .modal-gif {
    margin-top: 24px;
    text-align: center;

    @media (max-width: 768px) {
      display: none;
    }

    img {
      max-width: 200px;
      width: 100%;
      border-radius: 0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
